/**
 * WebSocket连接管理类
 * 提供自动连接、重连、消息发送等功能
 */
class createWebSocket {
  constructor() {
    this.ws = null; // WebSocket实例
    this.isConnected = false; // 连接状态标记
    this.messageCallback = null; // 消息接收回调函数
    this.errorCallback = null; // 错误回调函数
    this.lastConfig = null; // 保存配置用于重连
  }

  /**
   * 初始化WebSocket连接
   * @param {Object} config - 连接配置
   * @param {string} config.url - WebSocket服务器地址
   * @param {string} config.path - WebSocket路径
   * @param {boolean} config.secure - 是否使用wss协议
   * @returns {Promise<WebSocket>} WebSocket实例
   */
  async initWebSocket(config) {
    try {
      // 保存配置用于重连
      this.lastConfig = config;

      // 构建WebSocket URL，支持ws/wss协议
      const protocol = config.secure ? 'wss' : 'ws';
      const wsUrl = `${protocol}://${config.url}${config.path}`;
      this.ws = new WebSocket(wsUrl);

      // 设置事件监听器
      this.setupEventListeners();

      // 等待连接建立
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('连接超时'));
        }, 10000); // 10秒超时
        // WebSocket连接已建立
        this.ws.onopen = (event) => {
          this.isConnected = true;
          clearTimeout(timeout);
          resolve(this.ws);
        };

        this.ws.onerror = (error) => {
          clearTimeout(timeout);
          this.isConnected = false;
          reject(error);
        };
      });
    } catch {
      throw new Error('初始化WebSocket连接失败');
    }
  }

  // 设置WebSocket事件监听器
  setupEventListeners() {
    if (!this.ws) return;

    // 监听服务器消息
    this.ws.onmessage = (event) => {
      // 如果有消息回调函数，则调用
      if (this.messageCallback) {
        this.messageCallback(event);
      }
    };

    // 连接关闭事件
    this.ws.onclose = (event) => {
      this.isConnected = false;
    };

    // 连接错误事件
    this.ws.onerror = (error) => {
      this.isConnected = false;
      // 如果有错误回调函数，则调用
      if (this.errorCallback) {
        this.errorCallback(error);
      }
    };
  }

  // 关闭WebSocket连接
  closeWebSocket() {
    if (!this.ws) return;
    this.isConnected = false;
    this.ws.close();
  }

  /**
   * 发送消息（自动处理连接状态）
   * @param {Object|string} message - 要发送的消息
   * @returns {Promise<boolean>} 发送是否成功
   */
  async sendMessage(message) {
    try {
      // 检查连接状态，如果未连接则尝试重连
      if (!this.isWebSocketConnected()) {
        if (!await this.ensureConnected()) {
          return false;
        }
      }

      // 根据消息类型进行发送
      if (typeof message === 'object') {
        this.ws.send(JSON.stringify(message));
      } else {
        this.ws.send(message);
      }
      return true;
    } catch {
      return false;
    }
  }

  /**
   * 确保连接可用（自动重连）
   * @returns {Promise<boolean>} 连接是否成功
   */
  async ensureConnected() {
    // 如果已连接，直接返回
    if (this.isWebSocketConnected()) {
      return true;
    }

    try {
      // 如果WebSocket实例存在但已关闭，先关闭
      if (this.ws) {
        this.ws.close();
      }

      // 如果有保存的配置，则重新连接
      if (this.lastConfig) {
        await this.initWebSocket(this.lastConfig);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  /**
   * 设置消息接收回调函数
   * @param {Function} callback - 消息回调函数
   */
  onMessage(callback) {
    this.messageCallback = callback;
  }

  /**
   * 设置错误处理回调函数
   * @param {Function} callback - 错误回调函数
   */
  onError(callback) {
    this.errorCallback = callback;
  }

  /**
   * 检查WebSocket连接状态
   * @returns {boolean} 是否已连接
   */
  isWebSocketConnected() {
    return this.ws && this.isConnected && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * 静态方法：一键发送消息（类方法）
   * @param {createWebSocket} instance - WebSocket实例
   * @param {Object|string} message - 要发送的消息
   * @returns {Promise<boolean>} 发送是否成功
   */
  static async sendMessage(instance, message) {
    if (!instance) {
      throw new Error('WebSocket实例不存在');
    }
    return await instance.sendMessage(message);
  }

  /**
   * 便利方法：连接并发送消息（一步到位）
   * @param {Object} config - 连接配置
   * @param {string} config.url - 服务器地址
   * @param {string} config.path - 连接路径
   * @param {Object|string} message - 要发送的消息
   * @returns {Promise<boolean>} 发送是否成功
   */
  async connectAndSend(config, message) {
    try {
      // 如果未连接，先建立连接
      if (!this.isWebSocketConnected()) {
        await this.initWebSocket(config);
      }
      // 发送消息
      return await this.sendMessage(message);
    } catch {
      return false;
    }
  }
};

export default createWebSocket;
